import React, { useEffect } from 'react';
import { useTheme } from 'next-themes';

const PrivacyPolicy: React.FC = () => {
  const { theme } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-7xl mx-auto p-4">
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-center">Privacy Policy</h1>
                <p className="text-sm text-center">Effective Date: 02nd September 2024</p>
            </div>

            <div className="mb-4">
                <h2 className="text-lg font-bold">1. Introduction</h2>
                <p className="text-sm">
                    This Privacy Policy outlines how ASIMITH by Swingbell Labs ("we," "us," or "our") collects, uses, and protects your personal data when you use our Electronic Health Record (EHR) platform integrated with the National Health Claims Exchange (NHCX) for claims processing. Our platform is designed to support interoperability and is compliant with the Ayushman Bharat Digital Mission (ABDM) standards.
                </p>
                <p className="text-sm mt-2">
                    This privacy statement applies only to our website. Our site might contain links to other sites. Please be aware that Swingbell Labs is not responsible for the privacy practices of such other sites. We encourage you to read the privacy statements of each and every website that collects personal or practice information from you.
                </p>
            </div>

             <div className="mb-4">
                <h2 className="text-lg font-bold">2. Data Collection</h2>
                <ul style={{ listStylePosition: 'inside', padding: '0 20px' }}>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                            <strong>• Healthcare Data:</strong> We collect and process healthcare data provided by patients through doctors and hospitals using our platform. This data is owned by the patients and is collected with their consent.
                        </p>
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                            <strong>• Third-Party Services:</strong> Our platform utilizes third-party services, such as Amazon Web Services (AWS), for hosting and infrastructure. We do not collect personal data through these services but may use them for operational purposes.
                        </p>
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                            <strong>• Data Collection and Usage for KYC and Verification:</strong> As part of our KYC process, we collect and process personal information, including Aadhaar details, to verify the identity of medical doctors using our Platform. This data is collected in compliance with the guidelines set forth by the Unique Identification Authority of India (UIDAI) and is used solely for the purpose of identity verification.
                        </p>
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                        • Our Platform integrates with third-party services for: (a) Aadhaar-based KYC verification; (b) Contact information verification (mobile numbers, email addresses) and location tracking.
                        </p>
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                        • We are committed to safeguarding your personal data and have implemented appropriate measures to protect it. For information on how third-party services handle your data, please refer to their respective privacy policies. Your data will be used in accordance with applicable data protection laws and regulations.
                        </p>
                    </li>
                </ul>
            </div>

            <div className="mb-4">
                <h2 className="text-lg font-bold">3. Data Use</h2>
                <ul style={{ listStylePosition: 'inside', padding: '0 20px' }}>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                            <strong>• Claims Processing:</strong> We use the collected data to facilitate the submission of insurance claims through the NHCX. This includes processing, managing, and securely transferring data as required.
                        </p>
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                            <strong>• Interoperability:</strong> Our platform supports interoperability by integrating with ABDM-compliant gateways and APIs, allowing data to move between systems with patient consent.
                        </p>
                    </li>
                </ul>
            </div>

            {/* Data Sharing */}
            <div className="mb-4">
                <h2 className="text-lg font-bold">4. Data Sharing</h2>
                <ul style={{ listStylePosition: 'inside', padding: '0 20px' }}>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                            <strong>• With Partners:</strong> We may collaborate with other organizations, service providers, or agencies to onboard doctors and hospitals. These partners will front-end the platform with a "Powered by Swingbell Labs" byline, but we remain responsible for the underlying software and infrastructure.
                        </p>
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                            <strong>• Third-Party AI Tools:</strong> Our platform may include third-party AI tools for summarizing historical data. Users are advised to validate the outputs of these tools before relying on them for clinical or decision-making purposes. We are not responsible for the accuracy or reliability of these tools' outputs.
                        </p>
                    </li>
                </ul>
            </div>

            {/* Data Security */}
            <div className="mb-4">
                <h2 className="text-lg font-bold">5. Data Security</h2>
                <ul style={{ listStylePosition: 'inside', padding: '0 20px' }}>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                            <strong>• Security Measures:</strong> We implement reasonable security measures to protect data within our systems. However, no system is entirely secure, and we cannot be held liable for data loss or breaches beyond our control.
                        </p>
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                            <strong>• Third-Party Services:</strong> As our platform uses third-party services like AWS, we are not responsible for data loss or breaches occurring through these services.
                        </p>
                    </li>
                </ul>
            </div>

            {/* Data Ownership */}
            <div className="mb-4">
                <h2 className="text-lg font-bold">6. Data Ownership</h2>
                <p className="text-sm">
                    The patient retains ownership of their healthcare data. We ensure that data is moved between systems only with the patient's consent.
                </p>
            </div>

            {/* Compliance and Reporting */}
            <div className="mb-4">
                <h2 className="text-lg font-bold">7. Compliance and Reporting</h2>
                <ul style={{ listStylePosition: 'inside', padding: '0 20px' }}>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                            <strong>• Regulatory Compliance:</strong> We adhere to guidelines, policies, and notifications from the National Health Authority, including data management policies and security regulations.
                        </p>
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                        <p className="text-sm">
                            <strong>• Cybersecurity Incidents:</strong> We will report cybersecurity incidents and share related information in accordance with applicable laws and regulations.
                        </p>
                    </li>
                </ul>
            </div>
            <div className="mb-4">
                <h2 className="text-lg font-bold">8. Limitation of Liability</h2>
                <p className="text-sm">
                    We are not liable for any direct, special, incidental, consequential, or punitive damages arising from the use of our platform or any third-party services. Our total liability for any claims related to this Privacy Policy or the use of our platform shall not exceed the fees paid for the services during the preceding term.
                </p>
            </div>

            <div className="mb-4">
                <h2 className="text-lg font-bold">9. Business Transitions</h2>
                <p className="text-sm mb-2">
                    In the event that Swingbell Labs undergoes a business transition, such as a merger, acquisition, or sale of all or part of its assets, your personal data may be transferred as part of the transaction. We will notify you before your data is subject to a new privacy policy. If the company ceases operations, we will provide reasonable notice and options to retrieve or transfer your data, where legally permissible.
                </p>
                <p className="text-sm">
                    In the event of a merger, acquisition, or sale involving an international entity, we will ensure that your personal data is transferred in compliance with applicable global data protection laws, including the General Data Protection Regulation (GDPR) for EU-based data. We will notify you of any changes to our privacy practices and provide options to manage or transfer your data if required. Our commitment is to safeguard your data throughout the transition process.
                </p>
            </div>

            <div className="mb-4">
                <h2 className="text-lg font-bold">10. Updates to This Policy</h2>
                <p className="text-sm">
                We may update this Privacy Policy from time to time. Any changes will be posted on our platform, and your continued use of our services constitutes acceptance of the updated policy.
                </p>
            </div>

            <div className="mt-6">
                <h2 className="text-lg font-bold">11. Contact Us</h2>
                <p className="text-sm">
                    For any questions or concerns regarding this Privacy Policy, please contact us at:
                </p>
                <h6 className="text-sm font-bold">Swingbell Labs</h6>
                <a href="mailto:admin@swingbell.com" className="text-blue-500 hover:underline">
                    admin@swingbell.com
                </a>
            </div>
        </div>
    </div>
  );
};

export default PrivacyPolicy;